import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  CircleCheck,
  CircleX,
  Image as ImageIcon,
  Link as LinkIcon,
  X,
  Save,
  Tag,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

// Helper to extract a cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Safely parse event_config JSON string from backend
const parseEventConfig = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Split comma-separated string into trimmed array
const splitList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const emptyForm = {
  name: '',
  logo: '',
  trackLink: '',
  description: '',
  status: 'active',
  caps: '',
  steps: [],
  tnc: [],
  required_inputs: [],
  events: [],
};

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAnimating, setModalAnimating] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null); // null = create, object = edit
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAnimating, setDeleteAnimating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Tag input helper state (holds the "in-progress" typed value for each tag field)
  const [tagInputs, setTagInputs] = useState({ steps: '', tnc: '', required_inputs: '' });

  // ==================== FETCH OFFERS ====================
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.get(`${baseUrl}/api/admin/offers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data?.status) {
        setOffers(response.data.data || []);
      } else {
        toast.error(response.data?.msg || 'Failed to load offers');
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error(error.response?.data?.msg || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== MODAL HANDLERS ====================
  const openCreateModal = () => {
    setEditingOffer(null);
    setFormData(emptyForm);
    setTagInputs({ steps: '', tnc: '', required_inputs: '' });
    setIsModalOpen(true);
    requestAnimationFrame(() => setModalAnimating(true));
  };

  const openEditModal = (offer) => {
    const parsedEvents = parseEventConfig(offer.eventConfig || offer.event_config);
    setEditingOffer(offer);
    setFormData({
      name: offer.name || '',
      logo: offer.logo || '',
      trackLink: offer.trackLink || offer.track_link || '',
      description: offer.description || '',
      status: offer.status || 'active',
      caps: offer.caps || '',
      steps: splitList(offer.steps),
      tnc: splitList(offer.tnc),
      required_inputs: splitList(offer.required_inputs || offer.extraInputList),
      events: parsedEvents.map((e) => ({
        id: e.id || '',
        name: e.name || '',
        user: String(e.user ?? ''),
        refer: String(e.refer ?? ''),
      })),
    });
    setTagInputs({ steps: '', tnc: '', required_inputs: '' });
    setIsModalOpen(true);
    requestAnimationFrame(() => setModalAnimating(true));
  };

  const closeModal = () => {
    setModalAnimating(false);
    setTimeout(() => {
      setIsModalOpen(false);
      setEditingOffer(null);
      setFormData(emptyForm);
    }, 300);
  };

  // ==================== TAG INPUT HELPERS ====================
  const addTag = (field) => {
    const raw = tagInputs[field].trim();
    if (!raw) return;
    // Allow comma-separated bulk paste
    const newItems = splitList(raw);
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ...newItems.filter((n) => !prev[field].includes(n))],
    }));
    setTagInputs((prev) => ({ ...prev, [field]: '' }));
  };

  const removeTag = (field, idx) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== idx),
    }));
  };

  const handleTagKeyDown = (e, field) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(field);
    } else if (e.key === 'Backspace' && !tagInputs[field] && formData[field].length > 0) {
      removeTag(field, formData[field].length - 1);
    }
  };

  // ==================== EVENT ROW HANDLERS ====================
  const addEvent = () => {
    setFormData((prev) => ({
      ...prev,
      events: [...prev.events, { id: '', name: '', user: '', refer: '' }],
    }));
  };

  const updateEvent = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      events: prev.events.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    }));
  };

  const removeEvent = (idx) => {
    setFormData((prev) => ({
      ...prev,
      events: prev.events.filter((_, i) => i !== idx),
    }));
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Offer name is required');
      return;
    }
    if (!formData.trackLink.trim()) {
      toast.error('Tracking link is required');
      return;
    }

    setSubmitting(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const eventConfig = JSON.stringify(
        formData.events.map((ev) => ({
          id: ev.id,
          name: ev.name,
          user: ev.user,
          refer: ev.refer,
        }))
      );

      const payload = {
        name: formData.name.trim(),
        logo: formData.logo.trim(),
        trackLink: formData.trackLink.trim(),
        description: formData.description.trim(),
        status: formData.status,
        caps: formData.caps,
        steps: formData.steps.join(','),
        tnc: formData.tnc.join(','),
        required_inputs: formData.required_inputs,
        events: formData.events,
        eventConfig,
        extraInputList: formData.required_inputs.join(','),
      };

      const url = editingOffer
        ? `${baseUrl}/api/admin/offers/${editingOffer.offer_id}`
        : `${baseUrl}/api/admin/offers`;

      const method = editingOffer ? 'post' : 'post';

      const response = await axios[method](url, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data?.status) {
        toast.success(response.data.msg || `Offer ${editingOffer ? 'updated' : 'created'} successfully`);
        closeModal();
        fetchOffers();
      } else {
        toast.error(response.data?.msg || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error(error.response?.data?.msg || 'Failed to save offer');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== DELETE ====================
  const openDeleteConfirm = (offer) => {
    setDeleteTarget(offer);
    requestAnimationFrame(() => setDeleteAnimating(true));
  };

  const closeDeleteConfirm = () => {
    setDeleteAnimating(false);
    setTimeout(() => setDeleteTarget(null), 300);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.delete(
        `${baseUrl}/api/admin/offers/${deleteTarget.offer_id}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data?.status) {
        toast.success(response.data.msg || 'Offer deleted successfully');
        closeDeleteConfirm();
        fetchOffers();
      } else {
        toast.error(response.data?.msg || 'Failed to delete offer');
      }
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(error.response?.data?.msg || 'Failed to delete offer');
    } finally {
      setDeleting(false);
    }
  };

  // ==================== FILTERED OFFERS ====================
  const filteredOffers = useMemo(() => {
    if (!searchQuery.trim()) return offers;
    const q = searchQuery.toLowerCase();
    return offers.filter(
      (o) =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.offer_id || '').toLowerCase().includes(q)
    );
  }, [offers, searchQuery]);

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in {
          animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes modalIn {
          0% { opacity: 0; transform: scale(0.94) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in {
          animation: modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }
        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }
      `}</style>

      <div className="p-4 sm:p-8 space-y-6 max-w-[1600px] mx-auto mt-2">

        {/* ==================== HEADER ==================== */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-spring-in">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-zinc-100">
              Campaign Manager
            </h2>
            <p className="text-zinc-500 text-sm font-medium mt-1">
              Create and control network offers
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                placeholder="Search campaign..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-zinc-900/90 border border-zinc-800 rounded-2xl focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 font-bold text-sm shadow-sm text-zinc-100 placeholder:text-zinc-600 smooth-transition"
              />
            </div>
            <button
              onClick={openCreateModal}
              className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-black flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/20 smooth-transition micro-interaction"
            >
              <Plus className="w-5 h-5" />
              Add Offer
            </button>
          </div>
        </div>

        {/* ==================== DESKTOP TABLE ==================== */}
        <div className="hidden lg:block bg-zinc-900/90 border border-zinc-800 rounded-[2.5rem] shadow-lg overflow-hidden animate-spring-in">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-zinc-950/50 border-b border-zinc-800">
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-zinc-500">Offer Info</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-zinc-500">Payout Status</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-zinc-500 text-center">Status</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-zinc-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/60">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-zinc-800 shimmer-bg" />
                        <div className="space-y-2">
                          <div className="w-32 h-4 bg-zinc-800 rounded shimmer-bg" />
                          <div className="w-20 h-3 bg-zinc-800 rounded shimmer-bg" />
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6"><div className="w-32 h-4 bg-zinc-800 rounded shimmer-bg" /></td>
                    <td className="px-8 py-6 text-center"><div className="w-16 h-6 bg-zinc-800 rounded-lg mx-auto shimmer-bg" /></td>
                    <td className="px-8 py-6 text-right"><div className="w-20 h-8 bg-zinc-800 rounded-2xl ml-auto shimmer-bg" /></td>
                  </tr>
                ))
              ) : filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-8 py-16 text-center">
                    <p className="text-sm font-bold text-zinc-500 italic">No offers found.</p>
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer) => {
                  const events = parseEventConfig(offer.eventConfig || offer.event_config);
                  const totalPayout = events.reduce((acc, e) => acc + Number(e.user || 0), 0);
                  return (
                    <tr key={offer.offer_id} className="group hover:bg-zinc-950/40 transition-colors duration-200">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden shrink-0">
                            {offer.logo ? (
                              <img
                                src={offer.logo}
                                alt={offer.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(offer.name || 'O') + '&background=059669&color=fff';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-zinc-600">
                                <Tag className="w-5 h-5" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-black text-zinc-100 group-hover:text-emerald-400 transition-colors">
                              {offer.name}
                            </p>
                            <p className="text-[10px] font-bold text-zinc-500">
                              ID: #{offer.offer_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-sm font-black text-emerald-400">
                          {totalPayout > 0 ? `₹${totalPayout} Total` : 'Dynamic Configuration'}
                        </span>
                        <p className="text-[10px] font-bold text-zinc-500">
                          Caps: {offer.caps || 'Unlimited'}
                        </p>
                      </td>
                      <td className="px-8 py-6 text-center">
                        {offer.status === 'active' ? (
                          <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-black uppercase inline-flex items-center gap-1.5">
                            <CircleCheck className="w-3 h-3" /> Active
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-lg text-[10px] font-black uppercase inline-flex items-center gap-1.5">
                            <CircleX className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-8 py-6 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditModal(offer)}
                            aria-label="Edit offer"
                            className="p-3 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-2xl hover:bg-blue-500 hover:text-white smooth-transition micro-interaction"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(offer)}
                            aria-label="Delete offer"
                            className="p-3 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-2xl hover:bg-rose-500 hover:text-white smooth-transition micro-interaction"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ==================== MOBILE CARDS ==================== */}
        <div className="grid grid-cols-1 gap-4 lg:hidden">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-zinc-900/90 p-5 rounded-[2rem] border border-zinc-800 shadow-lg shimmer-bg h-40" />
            ))
          ) : filteredOffers.length === 0 ? (
            <div className="bg-zinc-900/90 p-10 rounded-[2rem] border border-zinc-800 text-center">
              <p className="text-sm font-bold text-zinc-500 italic">No offers found.</p>
            </div>
          ) : (
            filteredOffers.map((offer) => {
              const events = parseEventConfig(offer.eventConfig || offer.event_config);
              const totalPayout = events.reduce((acc, e) => acc + Number(e.user || 0), 0);
              return (
                <div key={offer.offer_id} className="bg-zinc-900/90 p-5 rounded-[2rem] border border-zinc-800 shadow-lg animate-spring-in">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden shrink-0">
                        {offer.logo ? (
                          <img
                            src={offer.logo}
                            alt={offer.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(offer.name || 'O') + '&background=059669&color=fff';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-600">
                            <Tag className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-black text-zinc-100 truncate">{offer.name}</h4>
                        <p className="text-[10px] font-bold text-zinc-500">ID: #{offer.offer_id}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => openEditModal(offer)}
                        aria-label="Edit"
                        className="p-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-xl micro-interaction"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(offer)}
                        aria-label="Delete"
                        className="p-2 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-xl micro-interaction"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-zinc-950/60 p-3 rounded-2xl flex justify-between items-center">
                    <span className="text-[10px] font-black uppercase text-zinc-500 tracking-widest">
                      {totalPayout > 0 ? `₹${totalPayout} Total` : 'Cap: None'}
                    </span>
                    <span className={`text-[10px] font-black uppercase ${offer.status === 'active' ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {offer.status}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>

      {/* ==================== CREATE/EDIT OFFER MODAL ==================== */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            onClick={closeModal}
            className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${modalAnimating ? 'opacity-100' : 'opacity-0'}`}
          />

          <div
            className={`relative w-full max-w-4xl bg-zinc-900 rounded-[2.5rem] border border-zinc-800 shadow-2xl overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh] transition-all duration-300 ${modalAnimating ? 'animate-modal-in' : 'opacity-0 scale-95'}`}
          >
            {/* Header */}
            <div className="p-6 sm:p-8 border-b border-zinc-800 flex justify-between items-center bg-zinc-950/50 shrink-0">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-zinc-100">
                  {editingOffer ? 'Edit Offer' : 'Create New Offer'}
                </h2>
                <p className="text-[10px] sm:text-xs text-zinc-500 font-bold uppercase tracking-widest mt-0.5">
                  Campaign Configuration
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-rose-400 rounded-xl smooth-transition micro-interaction"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form
              id="offer-form"
              onSubmit={handleSubmit}
              className="p-6 sm:p-8 overflow-y-auto space-y-8 custom-scrollbar"
            >
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Offer Name
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Kotak 811"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-950/60 border border-zinc-800 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none font-bold text-sm text-zinc-100 placeholder:text-zinc-600"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Logo URL
                  </label>
                  <div className="relative">
                    <ImageIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type="url"
                      placeholder="https://..."
                      value={formData.logo}
                      onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-zinc-950/60 border border-zinc-800 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none font-bold text-sm text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Description
                  </label>
                  <textarea
                    placeholder="Brief offer description..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-950/60 border border-zinc-800 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none font-medium text-sm text-zinc-100 placeholder:text-zinc-600 min-h-[80px]"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Tracking Link
                  </label>
                  <div className="relative">
                    <LinkIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      required
                      type="url"
                      placeholder="https://tracking-url.com?p1={tid}"
                      value={formData.trackLink}
                      onChange={(e) => setFormData({ ...formData, trackLink: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 bg-zinc-950/60 border border-zinc-800 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none font-bold text-sm text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>
                </div>
              </div>

              {/* Event Payouts */}
              <div className="bg-zinc-950/60 p-4 sm:p-6 rounded-[2rem] border border-zinc-800 space-y-4">
                <div className="flex justify-between items-center px-1">
                  <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                    Event Payouts
                  </h3>
                  <button
                    type="button"
                    onClick={addEvent}
                    className="flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-[10px] font-black shadow-lg shadow-emerald-500/20 smooth-transition micro-interaction"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Event
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.events.length === 0 && (
                    <p className="text-center text-xs text-zinc-500 italic py-4">
                      No events added yet. Click "Add Event" to start.
                    </p>
                  )}

                  {formData.events.map((ev, idx) => (
                    <div key={idx} className="relative bg-zinc-900 p-4 pt-10 sm:pt-4 rounded-2xl border border-zinc-800 shadow-sm">
                      <button
                        type="button"
                        onClick={() => removeEvent(idx)}
                        className="absolute top-2 right-2 p-1.5 text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors sm:hidden"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <label className="text-[9px] font-bold text-zinc-500 uppercase sm:hidden">ID</label>
                          <input
                            placeholder="ID"
                            value={ev.id}
                            onChange={(e) => updateEvent(idx, 'id', e.target.value)}
                            className="w-full bg-zinc-950/80 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-bold text-zinc-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-bold text-zinc-500 uppercase sm:hidden">Name</label>
                          <input
                            placeholder="Name"
                            value={ev.name}
                            onChange={(e) => updateEvent(idx, 'name', e.target.value)}
                            className="w-full bg-zinc-950/80 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-bold text-zinc-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-bold text-zinc-500 uppercase sm:hidden">User ₹</label>
                          <input
                            type="number"
                            placeholder="User ₹"
                            value={ev.user}
                            onChange={(e) => updateEvent(idx, 'user', e.target.value)}
                            className="w-full bg-zinc-950/80 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-bold text-zinc-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                          />
                        </div>
                        <div className="space-y-1 relative">
                          <label className="text-[9px] font-bold text-zinc-500 uppercase sm:hidden">Refer ₹</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              placeholder="Refer ₹"
                              value={ev.refer}
                              onChange={(e) => updateEvent(idx, 'refer', e.target.value)}
                              className="w-full bg-zinc-950/80 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-bold text-zinc-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => removeEvent(idx)}
                              className="hidden sm:flex p-2 text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors items-center justify-center border border-zinc-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tag Inputs Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Required Inputs */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Required Input
                  </label>
                  <div className="p-3 bg-zinc-950/60 border border-zinc-800 rounded-[1.5rem] flex flex-wrap gap-2 min-h-[56px] focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
                    {formData.required_inputs.map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[11px] font-bold">
                        {tag}
                        <button type="button" onClick={() => removeTag('required_inputs', i)} className="hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      value={tagInputs.required_inputs}
                      onChange={(e) => setTagInputs({ ...tagInputs, required_inputs: e.target.value })}
                      onKeyDown={(e) => handleTagKeyDown(e, 'required_inputs')}
                      onBlur={() => addTag('required_inputs')}
                      placeholder={formData.required_inputs.length ? '' : 'e.g. Mobile Number...'}
                      className="border-none focus:ring-0 text-sm font-medium flex-1 min-w-[120px] bg-transparent outline-none text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Steps to Follow
                  </label>
                  <div className="p-3 bg-zinc-950/60 border border-zinc-800 rounded-[1.5rem] flex flex-wrap gap-2 min-h-[56px] focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
                    {formData.steps.map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-[11px] font-bold">
                        {tag}
                        <button type="button" onClick={() => removeTag('steps', i)} className="hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      value={tagInputs.steps}
                      onChange={(e) => setTagInputs({ ...tagInputs, steps: e.target.value })}
                      onKeyDown={(e) => handleTagKeyDown(e, 'steps')}
                      onBlur={() => addTag('steps')}
                      placeholder={formData.steps.length ? '' : 'Type & hit enter...'}
                      className="border-none focus:ring-0 text-sm font-medium flex-1 min-w-[120px] bg-transparent outline-none text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>
                </div>

                {/* TnC */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Terms &amp; Conditions
                  </label>
                  <div className="p-3 bg-zinc-950/60 border border-zinc-800 rounded-[1.5rem] flex flex-wrap gap-2 min-h-[56px] focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
                    {formData.tnc.map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-[11px] font-bold">
                        {tag}
                        <button type="button" onClick={() => removeTag('tnc', i)} className="hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      value={tagInputs.tnc}
                      onChange={(e) => setTagInputs({ ...tagInputs, tnc: e.target.value })}
                      onKeyDown={(e) => handleTagKeyDown(e, 'tnc')}
                      onBlur={() => addTag('tnc')}
                      placeholder={formData.tnc.length ? '' : 'Type & hit enter...'}
                      className="border-none focus:ring-0 text-sm font-medium flex-1 min-w-[120px] bg-transparent outline-none text-zinc-100 placeholder:text-zinc-600"
                    />
                  </div>
                </div>
              </div>

              {/* Status + Caps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-2">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Listing Status
                  </label>
                  <div className="relative">
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-3 bg-zinc-950/60 border border-zinc-800 rounded-2xl font-bold text-sm outline-none appearance-none cursor-pointer text-zinc-100"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Daily Cap
                  </label>
                  <input
                    type="number"
                    placeholder="0 = Unlimited"
                    value={formData.caps}
                    onChange={(e) => setFormData({ ...formData, caps: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-950/60 border border-zinc-800 rounded-2xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none font-bold text-sm text-zinc-100 placeholder:text-zinc-600"
                  />
                </div>
              </div>
            </form>

            {/* Footer */}
            <div className="p-6 sm:p-8 bg-zinc-950/50 border-t border-zinc-800 flex flex-col sm:flex-row justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeModal}
                className="w-full sm:w-auto px-8 py-3 text-zinc-400 font-bold hover:text-zinc-100 transition-colors text-sm order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="offer-form"
                disabled={submitting}
                className="w-full sm:w-auto px-10 py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-black rounded-2xl shadow-xl shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 text-sm order-1 sm:order-2 smooth-transition micro-interaction disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {submitting ? 'Saving...' : editingOffer ? 'Update Campaign' : 'Save Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DELETE CONFIRM MODAL ==================== */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
          <div
            onClick={closeDeleteConfirm}
            className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${deleteAnimating ? 'opacity-100' : 'opacity-0'}`}
          />

          <div
            className={`relative w-full max-w-sm bg-zinc-900 border border-rose-500/30 rounded-[2.5rem] shadow-2xl p-8 text-center space-y-5 transition-all duration-300 ${deleteAnimating ? 'animate-modal-in' : 'opacity-0 scale-95'}`}
          >
            <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight">Delete Offer?</h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                Are you sure you want to delete <span className="text-rose-400 font-bold">"{deleteTarget.name}"</span>?
                This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={closeDeleteConfirm}
                className="flex-1 py-3 bg-zinc-950 border border-zinc-800 text-zinc-300 rounded-xl font-black text-xs uppercase tracking-wider hover:border-zinc-700 smooth-transition micro-interaction"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-3 bg-rose-500 hover:bg-rose-400 text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-lg shadow-rose-500/20 smooth-transition micro-interaction disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminOffers;